import type { FC } from "react";
import { useParams } from "react-router";

import type { Action, ActionAction, AuthFeatureData, Pages } from "~/core";
import type { DetailedPage } from "~/core/services/page.service";
import { isClickAction } from "~/core/utils/action.utils";
import { CardGallery } from "~/features/card-gallery/CardGallery";
import { isCardGallery } from "~/features/card-gallery/isCardGallery";
import { isMainBanner } from "~/features/main-bannerV2/isMainBanner";
import { MainBannerV2 } from "~/features/main-bannerV2/MainBannerV2";
import { isSteps } from "~/features/steps/isSteps";
import { Steps } from "~/features/steps/Steps";
import { isTextBlock } from "~/features/text-block/isTextBlock";
import { TextBlock } from "~/features/text-block/TextBlock";
import { isWelcomeOfferBanner } from "~/features/welcome-offer-banner/isWelcomeOfferBanner";
import { WelcomeOfferBanner } from "~/features/welcome-offer-banner/WelcomeOfferBanner";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";

export type PageConstructorProps = {
  data: { page: DetailedPage; auth?: AuthFeatureData };
};

export const PageConstructor: FC<PageConstructorProps> = ({ data }) => {
  const param = useParams();
  const { getDirectusImage } = useDirectus();
  const { page, auth } = data;

  const getUrl = (detailedPage: Pages): string => {
    if (!detailedPage?.slug) {
      return `/${param.lang}`;
    }

    const { segment, slug } = detailedPage;
    if (segment) {
      return `/${param.lang}/${segment}/${slug}`;
    } else {
      return `/${param.lang}/${slug}`;
    }
  };

  const { gtm } = useGtmContext();

  const onCallAction = (action: ActionAction): void => {
    if (!isClickAction(action.item)) {
      return;
    }

    if (!window?.location) {
      return;
    }

    if (action.item.type === "signIn") {
      gtm.logInMain({
        eventTime: new Date(),
        playerID: null,
        screenName: "mainPage",
      });
      window.open(auth?.room_url ?? "/");
    }

    if (action.item.type === "signOut") {
      //
    }
  };

  const isAuthorized = false;

  const isActionInactive = (control: Action): boolean => {
    if (control.inactive_if?.includes("authorized") && isAuthorized) {
      return true;
    }

    if (control.inactive_if?.includes("unauthorized") && !isAuthorized) {
      return true;
    }

    return false;
  };

  return page?.translations?.[0]?.data?.map((component, index) => {
    const item = component?.item;

    if (!item) return null;

    if (isTextBlock(item)) {
      return <TextBlock data={item} key={index} />;
    }

    if (isMainBanner(item)) {
      return (
        <MainBannerV2
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isControlInactive={isActionInactive}
        />
      );
    }

    if (isSteps(item)) {
      return (
        <Steps
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          isControlInactive={isActionInactive}
        />
      );
    }

    if (isWelcomeOfferBanner(item)) {
      return (
        <WelcomeOfferBanner
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isActionInactive={isActionInactive}
        />
      );
    }
    if (isCardGallery(item)) {
      return (
        <CardGallery
          key={index}
          data={item}
          getUrl={getUrl}
          onCallAction={onCallAction}
          getImageUrl={getDirectusImage}
          isControlInactive={isActionInactive}
        />
      );
    }

    return null;
  });
};
